import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { formatDateTime } from '../../../shared/utils/localization';
import ConversationChannelBadge from '../../conversations/components/ConversationChannelBadge';
import { getConversationHeaderChannelIcons } from '../../conversations/conversationChannelIcons';
import type { Conversation } from '../../conversations/types/Conversation';
import { truncateMessageSnippet } from '../../conversations/utils/messageUtils';
import panelStyles from '../../consultations/components/RelatedConsultationsPanel.module.css';
import styles from './ContactConversationsPanel.module.css';

export type ContactConversationsPanelProps = {
  conversations: Conversation[];
  emptyLabel?: string;
  returnTo?: string;
};

const getLastMessage = (conversation: Conversation) => {
  if (!conversation.messages.length) {
    return undefined;
  }

  return [...conversation.messages].sort((left, right) => left.timestamp.localeCompare(right.timestamp)).at(-1);
};

const getConversationLabel = (conversation: Conversation, untitled: string): string => {
  const title = conversation.title?.trim();
  if (title) {
    return title;
  }

  const lastMessage = getLastMessage(conversation);
  const snippet = lastMessage?.content?.trim()
    ? truncateMessageSnippet(lastMessage.content, 72)
    : '';
  return snippet || untitled;
};

export const ContactConversationsPanel: React.FC<ContactConversationsPanelProps> = ({
  conversations,
  emptyLabel,
  returnTo,
}) => {
  const { t } = useTranslation(['contacts', 'conversations', 'common']);
  const navigate = useNavigate();

  if (conversations.length === 0) {
    return (
      <div className={panelStyles.emptyState}>
        {emptyLabel || t('noContactConversations', { ns: 'contacts', defaultValue: 'No conversations are linked to this contact.' })}
      </div>
    );
  }

  return (
    <div className={panelStyles.tableWrap}>
      <table className={panelStyles.table}>
        <thead>
          <tr>
            <th>{t('updated', { ns: 'common', defaultValue: 'Updated' })}</th>
            <th>{t('conversation', { ns: 'conversations', defaultValue: 'Conversation' })}</th>
            <th>{t('channel', { ns: 'conversations', defaultValue: 'Channel' })}</th>
            <th>{t('lastMessage', { ns: 'conversations', defaultValue: 'Last message' })}</th>
          </tr>
        </thead>
        <tbody>
          {conversations.map((conversation) => {
            const channelIcon = getConversationHeaderChannelIcons(conversation, t as TFunction<'conversations'>)[0];
            const lastMessage = getLastMessage(conversation);
            return (
              <tr
                key={conversation.id}
                onClick={() => navigate(`/conversations/${conversation.id}`, returnTo ? { state: { returnTo } } : undefined)}
              >
                <td>{formatDateTime(conversation.updatedAt)}</td>
                <td>{getConversationLabel(conversation, t('untitledConversation', { ns: 'conversations', defaultValue: '(untitled)' }))}</td>
                <td>
                  {channelIcon ? (
                    <span className={styles.channelCell}>
                      <ConversationChannelBadge icon={channelIcon} compact disableTooltip />
                      <span>{channelIcon.label}</span>
                    </span>
                  ) : '-'}
                </td>
                <td>
                  {lastMessage?.content?.trim()
                    ? truncateMessageSnippet(lastMessage.content, 80)
                    : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ContactConversationsPanel;
